import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { instance } from 'api/axios.instance';
import { localList } from 'app/constants';
import { selectDevice } from 'store/slices/common';
import { Box, Button, Flex, Heading, Separator, Text } from '@radix-ui/themes';
import SocialShare from 'components/common/socialShare';

import './style.scss';

export default function BoardDetail({ boardType }) {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  // hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // state
  const data = location.state?.fetchBody;

  const [detailData, setDetailData] = useState({});
  const fetchPostDetail = useCallback(async () => {
    const response = await instance(
      `/post/detail/${id}`,
      data
        ? {
            method: 'POST',
            data: {
              ...data
            }
          }
        : { method: 'GET' }
    );
    setDetailData(response.data.data);
  }, [id, data]);

  useEffect(() => {
    fetchPostDetail();
  }, [fetchPostDetail]);

  const handleDownload = async file => {
    try {
      const userAgent = navigator.userAgent || window.opera;
      const isNaverApp = /NAVER/i.test(userAgent);
      const isMobile = /android|iphone|ipad|ipod/i.test(userAgent);

      // 직접 링크 방식 사용
      if (isNaverApp || (isMobile && !window.chrome)) {
        const link = document.createElement('a');
        link.href = file.fileUrl;
        link.setAttribute('download', file.originalName);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // 기존 blob 방식 사용
        const response = await fetch(file.fileUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', file.originalName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error('Download error:', error);
      window.open(file.fileUrl, '_blank');
    }
  };

  const goBack = () => {
    navigate(`/${boardType}`);
  };

  // 웹 접근성을 고려한 이미지 대체 텍스트 추가 함수
  const [parsedContent, setParsedContent] = useState('');
  const parseContentWithAlt = (htmlString = '') => {
    if (!htmlString.trim()) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const postTitle = detailData.post?.title || '';

    doc.querySelectorAll('img').forEach(img => {
      const currentAlt = img.getAttribute('alt')?.trim();
      const isFileName = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(currentAlt);

      // 의미있는 대체텍스트 결정: 유효한 alt가 있으면 유지, 없거나 파일명이면 게시글 제목 사용
      const altText = !currentAlt || isFileName ? postTitle : currentAlt;

      // figure 태그 생성
      const figure = doc.createElement('figure');

      // alt 속성에 의미있는 내용으로 설정
      img.setAttribute('alt', altText);

      // img를 figure로 교체
      img.replaceWith(figure);
      figure.appendChild(img);

      // 대체텍스트가 길 경우(10자 초과) figcaption으로 대체콘텐츠 추가 제공
      // sr-only 클래스로 시각적으로는 감추되 스크린리더에서 인식 가능하게 처리 (display:none, visibility:hidden 사용 불가)
      if (altText.length > 10) {
        const figcaption = doc.createElement('figcaption');
        figcaption.textContent = altText;
        figcaption.className = 'sr-only';
        figure.appendChild(figcaption);
      }
    });

    doc.querySelectorAll('a[target="_blank"]').forEach(link => {
      const title = link.getAttribute('title');

      if (!title || !title.trim()) {
        link.setAttribute('title', '새 창 열림');
      }
    });

    return doc.body.innerHTML;
  };

  useEffect(() => {
    if (detailData.post?.content) {
      setParsedContent(parseContentWithAlt(detailData.post.content));
    }
  }, [detailData.post?.content]);

  // 게시글 등록일 시간 부분 삭제
  const createPostDate = detailData.post?.createDate.slice(0, 10);

  return (
    <>
      <Box className="post-title">
        <Flex direction={isMobile && 'column'} justify="center" align="center" gap={isMobile ? '2' : '6'}>
          <Box className={`chips local ${localList.find(local => local.name === detailData.post?.region)?.en}`}>
            <Text>{detailData.post?.region}</Text>
          </Box>
          <Heading as="h3" className={isMobile ? 'sub-title' : 'title3'}>
            {detailData.post?.title}
          </Heading>
        </Flex>
      </Box>

      <Box className="post-info">
        <Text as="p">등록일 : {createPostDate}</Text>
        <Text as="p">조회수 : {detailData.post?.viewCount}</Text>
      </Box>

      <Box className="post-box">
        <Box className="post-content">
          <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
        </Box>
      </Box>

      <Box className="post-attachment">
        {/* 첨부파일 */}
        {detailData.files?.length > 0 && (
          <Flex direction={isMobile && 'column'}>
            <Box>
              <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-clip`}>
                첨부파일
              </Text>
            </Box>
            <ul className="attachment-list">
              {detailData.files.map((file, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => handleDownload(file)}
                    className="file-download-btn"
                    aria-label={`${file.originalName} 다운로드`}
                  >
                    <span className={`icon icon-${file.extension}`} aria-hidden="true" />
                    <Text as="p" className="file">
                      {file.originalName}
                    </Text>
                    <span className="download" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </Flex>
        )}
        {/* 관련링크 */}
        {detailData.post?.link && (
          <Flex direction={isMobile && 'column'}>
            <Box>
              <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-link`}>
                관련링크
              </Text>
            </Box>
            <ul className="attachment-list">
              <li>
                <a
                  href={detailData.post.link?.startsWith('http') ? detailData?.post.link : `https://${detailData.post?.link}`}
                  target="_blank"
                  color="gray"
                  rel="noreferrer"
                  title="새 창 열림"
                >
                  <Text as="p" className="link">
                    {detailData.post.link?.startsWith('http') ? detailData?.post.link : `https://${detailData.post?.link}`}
                  </Text>
                </a>
              </li>
            </ul>
          </Flex>
        )}
      </Box>

      {/* SNS 공유 기능 추가 */}
      <Flex direction={isMobile && 'column'} justify="end" align="center" className="sns-list">
        <SocialShare
          title={detailData.post?.title || '게시글'}
          url={window.location.href}
          description={detailData.post?.content?.replace(/<[^>]*>/g, '').substring(0, 100) + '...' || ''}
          imageUrl={detailData.thumbnails?.[0]?.fileUrl || ''}
        />
        <Button className="register" onClick={goBack}>
          목록
        </Button>
      </Flex>

      <Box className="post-navigation">
        {detailData.post?.prevPost && (
          <Box className="item">
            <Link to={`/${boardType}/detail/${detailData.post?.prevPost?.postNo}`}>
              <Text as="div" className="nav-label">
                이전글
                <Separator orientation={isMobile ? 'horizontal' : 'vertical'} className="divider" decorative />
              </Text>
              <Text as="p">{detailData.post?.prevPost?.title}</Text>
            </Link>
          </Box>
        )}
        {detailData.post?.nextPost && (
          <Box className="item">
            <Link to={`/${boardType}/detail/${detailData.post?.nextPost?.postNo}`}>
              <Text as="div" className="nav-label">
                다음글
                <Separator orientation={isMobile ? 'horizontal' : 'vertical'} className="divider" decorative />
              </Text>
              <Text as="p">{detailData.post?.nextPost?.title}</Text>
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
}
